import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva, type VariantProps } from 'class-variance-authority';
import { Info } from 'lucide-react';
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from 'components/ui/hover-card';
import { DocumentationDrawer } from 'components/ui/documentation-drawer';

import { cn } from 'lib/utils';

const labelVariants = cva(
    'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
);

export interface LabelProps
    extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> {
    info?: string;
    documentation?: {
        title: string;
        content: string | React.ReactNode;
    };
}

const Label = React.forwardRef<
    React.ElementRef<typeof LabelPrimitive.Root>,
    LabelProps & VariantProps<typeof labelVariants>
>(({ className, info, documentation, ...props }, ref) => {
    const renderInfoInline = info && info.length < 120;
    return (
        <>
            <div className="flex flex-row items-center gap-x-2">
                <LabelPrimitive.Root
                    ref={ref}
                    className={cn(labelVariants(), className)}
                    {...props}
                />
                {info && !renderInfoInline && (
                    <HoverCard openDelay={30}>
                        <HoverCardTrigger>
                            <Info className="h-4 w-4" />
                        </HoverCardTrigger>
                        <HoverCardContent className="text-sm">
                            {info}
                        </HoverCardContent>
                    </HoverCard>
                )}
                {documentation && (
                    <DocumentationDrawer
                        buttonText="Info"
                        buttonClassName="p-0 text-xs h-4 "
                        title={documentation.title}
                        content={documentation.content}
                    />
                )}
            </div>
            {info && renderInfoInline && (
                <p className="mt-1 text-sm text-muted-foreground">{info}</p>
            )}
        </>
    );
});
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
